/**
 * @group Utils
 * 
 * @summary
 * <blockquote>Processes fetch response into ApiResponse format with smart content-type detection.</blockquote>
 * 
 * Supports auto-detection of content types and explicit response type override.
 * 
 * @example
 * ```typescript
 * // Auto-detect content type (default)
 * const result = await processResponse<User>(fetchResponse);
 * result.match({
 *   ok: (apiResponse) => {
 *     console.log('Data:', apiResponse.data);
 *     // Check apiResponse.ok for HTTP error status codes
 *     if (!apiResponse.ok) {
 *       console.error('HTTP error:', apiResponse.status);
 *     }
 *   },
 *   err: (error) => {
 *     console.error('Failed to parse response:', error.errorMessage);
 *   }
 * });
 * 
 * // With explicit response type for binary data
 * const blobResult = await processResponse<Blob>(fetchResponse, 'blob');
 * ```
 */
import { AppError, AsyncResult, tryCatchAsync, ok, err } from '@shared/utils';
import type { Parser } from '../interfaces';
import type { ApiResponse, ResponseType } from '../types';
import { extractHeaders } from './extractHeaders';

/** @internal */
const jsonParser: Parser = {
  parse: async <T>(response: Response) => await response.json() as T
};

/** @internal */
const textParser: Parser = {
  parse: async <T>(response: Response) => await response.text() as T
};

/** @internal */
const blobParser: Parser = {
  parse: async <T>(response: Response) => await response.blob() as T
};

/** @internal */
const arrayBufferParser: Parser = {
  parse: async <T>(response: Response) => await response.arrayBuffer() as T
};

/** @internal */
const parsers: Record<Exclude<ResponseType, 'auto'>, Parser> = {
  arrayBuffer: arrayBufferParser,
  blob: blobParser,
  json: jsonParser,
  text: textParser
};

/** @internal */
const contentTypeMap = new Map<string, Parser>([
  ['application/json', jsonParser],
  ['text/', textParser],
  ['application/xml', textParser],
  ['application/javascript', textParser],
  ['application/octet-stream', blobParser],
  ['application/pdf', blobParser],
  ['image/', blobParser],
  ['audio/', blobParser],
  ['video/', blobParser]
]);

/** @internal */
function getParser(responseType: ResponseType, contentType: string | null): Parser {
  if (responseType !== 'auto') {
    return parsers[responseType];
  }
  
  if (contentType) {
    for (const [pattern, parser] of contentTypeMap) {
      if (contentType.includes(pattern)) {
        return parser;
      }
    }
  }
  
  return textParser;
}

/**
 * Processes a fetch Response into an ApiResponse.
 * 
 * @param response - The fetch Response object
 * @param responseType - How to parse the response body (default: 'auto')
 * @returns AsyncResult containing ApiResponse on success or AppError on parse failure
 * @internal
 */
export async function processResponse<T>(
  response: Response,
  responseType: ResponseType = 'auto'
): AsyncResult<ApiResponse<T>, AppError> {
  const headers = extractHeaders(response);
  
  if (response.status === 204) {
    return ok({
      data: null as T,
      status: response.status,
      statusText: response.statusText,
      headers,
      ok: response.ok,
      originalResponse: response,
    });
  }
  
  const parser = getParser(responseType, response.headers.get('content-type'));
  const parseResult = await tryCatchAsync(() => parser.parse<T>(response));
  
  if (parseResult.isErr) {
    return err({
      errorCode: 'RESPONSE_PROCESSING_FAILED',
      errorMessage: `Failed to process response: ${parseResult.getFail().errorMessage}`
    });
  }
  
  return ok({
    data: parseResult.value,
    status: response.status,
    statusText: response.statusText,
    headers,
    ok: response.ok,
    originalResponse: response,
  });
}
